import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, CheckBox, Button, Divider } from 'react-native-elements';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { View, Text, Alert, ActivityIndicator, Linking } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import DropDownPicker, { ItemType } from 'react-native-dropdown-picker';
import { get, isEmpty } from 'lodash';
import moment from 'moment';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useDispatch, useSelector } from 'react-redux';

import config from '../../../lib/config';
import { DescriptionField } from '../../../components/FormField';
import {
  createServiceRequestAPI,
  getServiceCategories,
  ServiceRequestConfig,
  ServiceRequestCategory,
} from '../../../api/adserver';
import { fetchServiceRequest } from '../../../store/appSlice';
import { RootState } from '../../../store/rootReducer';
import ServiceLine from '../../ledger/components/LedgerLine';
import * as Constant from '../../../lib/constants';
import CircleButton from '../components/CircleButton';
import styles from '../styles';
import EmergencyWarningOverlay from './EmergencyWarningOverlay';

const currentDate = moment().format('YYYY-MM-DD');
const validationSchema = Yup.object().shape({
  category: Yup.string().required('Please choose your category'),
  description: Yup.string().required('Please enter your description'),
});

interface Props {
  navigation: any;
}

interface FormFields {
  dropdownDisplay: string;
  date: string;
  category: string;
  subCategory: string;
  unit: string;
  briefDescription: string;
  description: string;
  allowSuiteAccess: boolean;
}

const CreateServiceRequestOverlay = ({ navigation }: Props) => {
  const dispatch = useDispatch();
  const serviceRequestConfigData = useSelector((state: RootState) => {
    const data = get(state, 'app.serviceRequestConfig', {});
    return data as {} as ServiceRequestConfig;
  });
  const [allowSuiteAccess, setAllowSuiteAccess] = useState(false);
  const [isCreateSuccess, setSuccess] = useState(false);
  const [unitId, setUnitId] = useState<any>('');
  const [serviceCategories, setCategories] = useState<ServiceRequestCategory[]>(
    [],
  );
  const [isWarningModalVisible, setIsWarningModalVisible] =
    useState<boolean>(true);
  // dropdown state
  const [open, setOpen] = React.useState(false);

  const tCode = useSelector((state) =>
    get(state, 'app.userData.Identification.attributes.IDValue', ''),
  );

  useEffect(() => {
    AsyncStorage.getItem(Constant.UNIT_ID)
      .then((value) => {
        setUnitId(value);
      })
      .catch(() => {
        setUnitId('');
      });
    getServiceCategories()
      .then(({ categories }) => {
        console.log('categories are', categories);
        setCategories(categories);
      })
      .catch(() => setCategories([]));
  }, [setUnitId]);

  const dropDowmItems = useMemo(() => {
    let serviceSubCategories: ItemType[] = [];
    serviceCategories.forEach((serviceCategory) => {
      if (serviceCategory.subCategories) {
        const formatedSubCategories = serviceCategory.subCategories.map(
          (subCategory) => ({
            parent: serviceCategory.id,
            value: `${serviceCategory.id}^${subCategory}`,
            label: `${serviceCategory.id} - ${subCategory}`,
          }),
        );
        serviceSubCategories = [
          ...serviceSubCategories,
          ...formatedSubCategories,
        ];
      }
    });

    let dropDownCategories: ItemType[] = (serviceCategories || []).map(
      (serviceCategory) => ({
        label: serviceCategory.name,
        value: serviceCategory.id,
        disabled: !isEmpty(serviceCategory.subCategories),
      }),
    );

    dropDownCategories = [...dropDownCategories, ...serviceSubCategories];
    return dropDownCategories;
  }, [serviceCategories]);

  const initialValue: FormFields = useMemo(() => {
    return {
      dropdownDisplay: '',
      date: currentDate,
      category: '',
      subCategory: '',
      unit: unitId,
      description: '',
      briefDescription: '',
      allowSuiteAccess: false,
    };
  }, [unitId]);

  const onCheckBoxChange = useCallback(
    (setFieldValue) => {
      const nextValue = !allowSuiteAccess;
      setFieldValue('allowSuiteAccess', nextValue);
      setAllowSuiteAccess(nextValue);
    },
    [allowSuiteAccess],
  );

  const onSubmit = useCallback(
    async (values: FormFields, actions) => {
      try {
        const params = {
          category: get(values, 'category', ''),
          sub_category: get(values, 'subCategory', ''),
          description: get(values, 'description', ''),
          brief_description: get(values, 'briefDescription', ''),
          allow_access: get(values, 'allowSuiteAccess', false),
        };
        await createServiceRequestAPI(params);
        dispatch(fetchServiceRequest());
        setSuccess(true);
        actions.setSubmitting(false);
      } catch (error) {
        const errorMess = get(
          error,
          'body.message',
          'Failed to create service request.\n\nPlease try again shortly.\n\nContact property management if the problem persists.',
        );
        const status = get(error, 'response.status', 'unkown error');
        Alert.alert('Error', `${errorMess}\n\nStatus: ${status}`, [
          { text: 'OK', onPress: () => {} },
        ]);
        actions.setSubmitting(false);
      }
    },
    [dispatch],
  );

  const onHideModal = useCallback(() => {
    setSuccess(false);
    navigation.goBack();
  }, [navigation]);

  return (
    <View style={styles.container}>
      <EmergencyWarningOverlay
        modalVisible={isWarningModalVisible}
        setModalVisible={setIsWarningModalVisible}
      />
      <Formik
        initialValues={initialValue}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          values,
          errors,
        }) => {
          return (
            <KeyboardAwareScrollView
              keyboardShouldPersistTaps={'handled'}
              style={styles.modalContent}
            >
              {isSubmitting ? (
                <Icon
                  style={styles.offscreenLeft}
                  name="close"
                  size={30}
                  tvParallaxProperties={null}
                />
              ) : (
                <Icon
                  onPress={() => !isSubmitting && onHideModal()}
                  iconStyle={styles.modalTopClose}
                  name="close"
                  tvParallaxProperties={null}
                />
              )}
              {!isCreateSuccess ? (
                <>
                  <Text style={styles.modalTitle}>New Service Request</Text>
                  <View style={styles.modalBody}>
                    {isEmpty(serviceRequestConfigData) ? (
                      <View />
                    ) : (
                      <View>
                        <Divider />
                        <Text style={styles.callCenterHeading}>
                          {serviceRequestConfigData?.call_center_heading}
                        </Text>
                        <Button
                          title={`${serviceRequestConfigData?.call_center_button_title} \n ${serviceRequestConfigData?.call_center_phone_number}`}
                          onPress={() => {
                            Linking.openURL(
                              `tel:${serviceRequestConfigData?.call_center_phone_number}`,
                            );
                          }}
                          style={styles.callCenterButton}
                        />
                        <Divider />
                      </View>
                    )}
                    <View style={styles.headerRequest}>
                      <ServiceLine
                        customTitleStyle={styles.serviceLine}
                        title="Date"
                        label={currentDate}
                      />
                      <ServiceLine
                        customTitleStyle={styles.serviceLine}
                        title="Unit#"
                        label={unitId}
                      />
                    </View>
                    <ServiceLine
                      customTitleStyle={styles.serviceLine}
                      title="Category"
                    />
                    <DropDownPicker
                      zIndex={50000}
                      zIndexInverse={60000}
                      items={dropDowmItems}
                      loading={
                        !dropDowmItems ||
                        !Array.isArray(dropDowmItems) ||
                        dropDowmItems.length < 1
                      }
                      placeholder="Please Select"
                      containerStyle={styles.dropDownContainer}
                      style={styles.dropDownStyle}
                      open={open}
                      setOpen={setOpen}
                      value={values.dropdownDisplay}
                      disabledItemLabelStyle={styles.disabledText}
                      setValue={(item) => {
                        const value: string = item();
                        if (value.includes('^')) {
                          const [category, subCategory] = value.split('^');
                          setFieldValue('category', category);
                          setFieldValue('subCategory', subCategory);
                          setFieldValue('dropdownDisplay', value);
                        } else {
                          setFieldValue('category', value);
                          setFieldValue('subCategory', '');
                          setFieldValue('dropdownDisplay', value);
                        }
                      }}
                      listMode={'MODAL'}
                      itemSeparator
                    />
                    {touched.category && errors.category && !isSubmitting && (
                      <Text style={styles.error}>{errors.category}</Text>
                    )}
                    <DescriptionField
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      customContainerStyle={styles.descriptionContainer}
                      name="briefDescription"
                      customLabelStyle={styles.description}
                      label="Brief Description"
                      error={errors.briefDescription}
                      value={values.briefDescription}
                      maxLength={30}
                      placeholder="Provide a few key words to summarize your service request"
                    />
                    <DescriptionField
                      name="description"
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched.description}
                      customContainerStyle={styles.descriptionContainer}
                      customLabelStyle={styles.description}
                      label="Description"
                      error={errors.description}
                      value={values.description}
                      placeholder="Provide as many details as possible relating to your service request"
                    />
                    <CheckBox
                      onPress={() => onCheckBoxChange(setFieldValue)}
                      iconType="ionicon"
                      uncheckedIcon="square-outline"
                      checkedIcon="checkbox-outline"
                      textStyle={styles.checkboxTitle}
                      containerStyle={styles.checkbox}
                      style={styles.checkboxContainer}
                      checkedColor={config.theme.headingTextColor}
                      checked={allowSuiteAccess}
                      title="Allow Suite Access"
                    />
                    {isSubmitting ? (
                      <ActivityIndicator
                        size={26}
                        color={config.theme.brandColor}
                      />
                    ) : (
                      <CircleButton
                        customStyle={styles.circleBototm}
                        iconName={'arrow-forward'}
                        disabled={isSubmitting}
                        onPress={handleSubmit}
                      />
                    )}
                  </View>
                </>
              ) : (
                <View>
                  <Text style={[styles.modalTitle, styles.successTitle]}>
                    Received!
                  </Text>
                  <Text style={styles.successMessage}>
                    Thank you for submitting your Service request. We will
                    follow up with you as soon as possible.
                  </Text>
                  <CircleButton
                    customStyle={[styles.circleBototm, styles.circleSuccess]}
                    iconName={'close'}
                    onPress={onHideModal}
                  />
                </View>
              )}
            </KeyboardAwareScrollView>
          );
        }}
      </Formik>
    </View>
  );
};

export default CreateServiceRequestOverlay;
